import { useState, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useTasksStore, useSettingsStore } from '@/store'
import { formatDateLocalized, englishToFarsiNumber } from '@/lib/calendar'
import { JalaliDatePicker } from '@/components/ui/JalaliDatePicker'
import dayjs from 'dayjs'

type Category = 'general' | 'study' | 'work'
type Priority = 'low' | 'medium' | 'high'
type FilterCategory = Category | 'all'
type FilterPriority = Priority | 'all'
type FilterStatus = 'all' | 'pending' | 'completed'
type ViewMode = 'list' | 'priority'

const CATEGORIES: readonly Category[] = ['general', 'study', 'work'] as const
const PRIORITIES: readonly Priority[] = ['high', 'medium', 'low'] as const
const DAYS_FA = ['شنبه', 'یکشنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنج‌شنبه', 'جمعه']

const getPriorityStars = (priority: Priority): string => {
  switch (priority) {
    case 'high': return '⭐⭐⭐'
    case 'medium': return '⭐⭐'
    case 'low': return '⭐'
  }
}

const getDayNameFa = (dayOfWeek: number): string => {
  const jalaliIndex = (dayOfWeek + 1) % 7
  return DAYS_FA[jalaliIndex]
}

const PRIORITY_COLOR = {
  high: 'border-red-500 bg-red-50 dark:bg-red-900/20',
  medium: 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20',
  low: 'border-green-500 bg-green-50 dark:bg-green-900/20',
}

export default function TasksPlanner(): JSX.Element {
  const { t, i18n } = useTranslation()
  const { calendar, language } = useSettingsStore()
  const { tasks, addTask, toggleTask, deleteTask } = useTasksStore()

  const [viewMode, setViewMode] = useState<ViewMode>('list')
  const [filterCategory, setFilterCategory] = useState<FilterCategory>('all')
  const [filterPriority, setFilterPriority] = useState<FilterPriority>('all')
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all')
  const [newTaskTitle, setNewTaskTitle] = useState('')
  const [newTaskCategory, setNewTaskCategory] = useState<Category>('general')
  const [newTaskPriority, setNewTaskPriority] = useState<Priority>('medium')
  const [newTaskDate, setNewTaskDate] = useState(() => dayjs().format('YYYY-MM-DD'))
  const [newTaskRecurrence, setNewTaskRecurrence] = useState<'none' | 'daily' | 'weekly' | 'monthly'>('none')
  const [newTaskRecurringDays, setNewTaskRecurringDays] = useState<number[]>([])
  const [newTaskRecurringEndDate, setNewTaskRecurringEndDate] = useState<string>('')

  const getCategoryLabel = (category: Category): string => {
    const labels: Record<Category, string> = {
      general: 'عمومی',
      study: 'درسی',
      work: 'کاری',
    }
    return labels[category]
  }

  const getPriorityLabel = (priority: Priority): string => {
    const labels: Record<Priority, string> = {
      low: 'پایین',
      medium: 'متوسط',
      high: 'بالا',
    }
    return labels[priority]
  }

  const filteredTasks = useMemo(() => {
    if (!Array.isArray(tasks)) return []
    return tasks.filter((task) => {
      if (filterCategory !== 'all' && task.category !== filterCategory) return false
      if (filterPriority !== 'all' && task.priority !== filterPriority) return false
      if (filterStatus === 'pending' && task.done) return false
      if (filterStatus === 'completed' && !task.done) return false
      return true
    })
  }, [tasks, filterCategory, filterPriority, filterStatus])

  const stats = useMemo(() => {
    if (!Array.isArray(tasks)) return { total: 0, completed: 0, pending: 0, percentage: 0 }
    const completed = tasks.filter(t => t.done).length
    const total = tasks.length
    return {
      total,
      completed,
      pending: total - completed,
      percentage: total === 0 ? 0 : Math.round((completed / total) * 100)
    }
  }, [tasks])

  const handleAddTask = useCallback((e?: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault()
    if (newTaskTitle.trim()) {
      addTask({
        title: newTaskTitle,
        category: newTaskCategory,
        priority: newTaskPriority,
        date: newTaskDate,
        recurrence: newTaskRecurrence,
        recurringDays: newTaskRecurrence === 'weekly' ? newTaskRecurringDays : undefined,
        recurringEndDate: (newTaskRecurrence !== 'none' && newTaskRecurringEndDate) ? newTaskRecurringEndDate : undefined,
        done: false,
      })
      setNewTaskTitle('')
      setNewTaskCategory('general')
      setNewTaskPriority('medium')
      setNewTaskDate(dayjs().format('YYYY-MM-DD'))
      setNewTaskRecurrence('none')
      setNewTaskRecurringDays([])
      setNewTaskRecurringEndDate('')
    }
  }, [newTaskTitle, newTaskCategory, newTaskPriority, newTaskDate, newTaskRecurrence, newTaskRecurringDays, newTaskRecurringEndDate, addTask])

  // Grouped tasks by date for list view
  const tasksByDate = useMemo(() => {
    const grouped: Record<string, typeof filteredTasks> = {}
    filteredTasks.forEach(task => {
      const dateKey = task.date || dayjs().format('YYYY-MM-DD')
      if (!grouped[dateKey]) grouped[dateKey] = []
      grouped[dateKey].push(task)
    })
    return Object.entries(grouped).sort(([a], [b]) => b.localeCompare(a))
  }, [filteredTasks])

  // Grouped tasks by priority for priority view
  const tasksByPriority = useMemo(() => {
    const grouped: Record<Priority, typeof filteredTasks> = {
      high: [],
      medium: [],
      low: [],
    }
    filteredTasks.forEach(task => {
      grouped[task.priority].push(task)
    })
    return grouped
  }, [filteredTasks])

  return (
    <div className="h-full flex flex-col fade-in bg-gray-100 dark:bg-slate-900">
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-slate-800">
        <h1 className="text-4xl font-bold text-gray-800 dark:text-white">
          📝 {t('sidebar.tasks')}
        </h1>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto scrollbar-custom px-6 py-6 bg-gray-100 dark:bg-slate-900 flex flex-col">

      {/* Stats Display */}
      <div className="flex-shrink-0 grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg neumorphic p-4 border-r-4 border-blue-500">
          <p className="text-sm text-gray-600 dark:text-gray-400">کل وظایف</p>
          <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">{i18n.language === 'fa' ? englishToFarsiNumber(stats.total) : stats.total}</p>
        </div>
        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg neumorphic p-4 border-r-4 border-green-500">
          <p className="text-sm text-gray-600 dark:text-gray-400">انجام‌شده</p>
          <p className="text-3xl font-bold text-green-600 dark:text-green-400">{i18n.language === 'fa' ? englishToFarsiNumber(stats.completed) : stats.completed}</p>
        </div>
        <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg neumorphic p-4 border-r-4 border-yellow-500">
          <p className="text-sm text-gray-600 dark:text-gray-400">درحال‌انجام</p>
          <p className="text-3xl font-bold text-yellow-600 dark:text-yellow-400">{i18n.language === 'fa' ? englishToFarsiNumber(stats.pending) : stats.pending}</p>
        </div>
        <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg neumorphic p-4 border-r-4 border-purple-500">
          <p className="text-sm text-gray-600 dark:text-gray-400">درصد تکمیل</p>
          <p className="text-3xl font-bold text-purple-600 dark:text-purple-400">{i18n.language === 'fa' ? englishToFarsiNumber(stats.percentage) : stats.percentage}%</p>
        </div>
      </div>

      {/* Add Task Form */}
      <div className="flex-shrink-0 bg-white dark:bg-slate-800 rounded-lg neumorphic p-6 mb-6">
        <h2 className="text-xl font-bold mb-4 text-gray-800 dark:text-white">{t('tasks.newTask')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <input
            type="text"
            placeholder="نام وظیفه"
            value={newTaskTitle}
            onChange={(e) => setNewTaskTitle(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-tasks dark:text-white md:col-span-2"
          />
          <select
            value={newTaskCategory}
            onChange={(e) => setNewTaskCategory(e.target.value as Category)}
            className="px-4 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-tasks dark:text-white"
          >
            {CATEGORIES.map(cat => (
              <option key={cat} value={cat}>{getCategoryLabel(cat)}</option>
            ))}
          </select>
          <select
            value={newTaskPriority}
            onChange={(e) => setNewTaskPriority(e.target.value as Priority)}
            className="px-4 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-tasks dark:text-white"
          >
            {PRIORITIES.map(pri => (
              <option key={pri} value={pri}>{getPriorityLabel(pri)}</option>
            ))}
          </select>
          
          {calendar === 'jalali' ? (
            <JalaliDatePicker
              value={newTaskDate}
              onChange={(date) => setNewTaskDate(date)}
            />
          ) : (
            <input
              type="date"
              value={newTaskDate}
              onChange={(e) => setNewTaskDate(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-tasks dark:text-white"
            />
          )}

          <select
            value={newTaskRecurrence}
            onChange={(e) => setNewTaskRecurrence(e.target.value as any)}
            className="px-4 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-tasks dark:text-white"
          >
            <option value="none">بدون تکرار</option>
            <option value="daily">هر روز</option>
            <option value="weekly">روزهای خاص هفته</option>
            <option value="monthly">هر ماه</option>
          </select>

          <button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              handleAddTask()
            }}
            className="bg-tasks text-white px-6 py-2 rounded-lg hover:opacity-90 transition-opacity font-semibold"
          >
            افزودن
          </button>
        </div>

        {/* Recurring Days Selector */}
        {newTaskRecurrence === 'weekly' && (
          <div className="mt-4 grid grid-cols-7 gap-2 mb-4">
            <p className="col-span-7 text-sm font-semibold text-gray-700 dark:text-gray-300">روزهای تکرار را انتخاب کنید:</p>
            {['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج'].map((day, index) => (
              <button
                key={index}
                type="button"
                onClick={() => {
                  setNewTaskRecurringDays(prev =>
                    prev.includes(index) ? prev.filter(d => d !== index) : [...prev, index]
                  )
                }}
                className={`px-2 py-2 rounded-lg font-semibold transition-all text-sm ${
                  newTaskRecurringDays.includes(index)
                    ? 'bg-tasks text-white'
                    : 'bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-gray-300'
                }`}
              >
                {day}
              </button>
            ))}
          </div>
        )}

        {/* Recurring End Date */}
        {newTaskRecurrence !== 'none' && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 block">
                تاریخ پایان تکرار (اختیاری):
              </label>
              {calendar === 'jalali' ? (
                <JalaliDatePicker
                  value={newTaskRecurringEndDate}
                  onChange={(date) => setNewTaskRecurringEndDate(date)}
                />
              ) : (
                <input
                  type="date"
                  value={newTaskRecurringEndDate}
                  onChange={(e) => setNewTaskRecurringEndDate(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-tasks dark:text-white w-full"
                />
              )}
            </div>
          </div>
        )}
      </div>

      {/* View Mode Selector */}
      <div className="flex-shrink-0 flex gap-2 mb-6">
        <button
          onClick={() => setViewMode('list')}
          className={`px-4 py-2 rounded-lg font-semibold transition-all ${
            viewMode === 'list'
              ? 'bg-tasks text-white'
              : 'bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-slate-700'
          }`}
        >
          لیست
        </button>
        <button
          onClick={() => setViewMode('priority')}
          className={`px-4 py-2 rounded-lg font-semibold transition-all ${
            viewMode === 'priority'
              ? 'bg-tasks text-white'
              : 'bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-slate-700'
          }`}
        >
          اولویت
        </button>
      </div>

      {/* Filters */}
      <div className="flex-shrink-0 bg-white dark:bg-slate-800 rounded-lg neumorphic p-4 mb-6 flex flex-wrap gap-4">
        <div className="flex items-center gap-2">
          <label className="text-sm font-semibold text-gray-700 dark:text-white">دسته:</label>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value as FilterCategory)}
            className="px-3 py-1 text-sm border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 rounded-lg focus:outline-none dark:text-white"
          >
            <option value="all">همه</option>
            {CATEGORIES.map(cat => (
              <option key={cat} value={cat}>{getCategoryLabel(cat)}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm font-semibold text-gray-700 dark:text-white">اولویت:</label>
          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value as FilterPriority)}
            className="px-3 py-1 text-sm border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 rounded-lg focus:outline-none dark:text-white"
          >
            <option value="all">همه</option>
            {PRIORITIES.map(pri => (
              <option key={pri} value={pri}>{getPriorityLabel(pri)}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm font-semibold text-gray-700 dark:text-white">وضعیت:</label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as FilterStatus)}
            className="px-3 py-1 text-sm border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 rounded-lg focus:outline-none dark:text-white"
          >
            <option value="all">همه</option>
            <option value="pending">درحال‌انجام</option>
            <option value="completed">انجام‌شده</option>
          </select>
        </div>
      </div>

      {/* Tasks Content */}
      <div className="flex-1 space-y-3">
        {filteredTasks.length === 0 ? (
          <div className="bg-white dark:bg-slate-800 rounded-lg neumorphic p-8 text-center">
            <p className="text-gray-500 dark:text-gray-400">هیچ وظیفه‌ای موجود نیست</p>
          </div>
        ) : (
          <>
            {viewMode === 'list' && (
              <div className="space-y-4">
                {tasksByDate.map(([dateKey, dateTasks]) => (
                  <div key={dateKey}>
                    <div className="sticky top-0 bg-slate-100 dark:bg-slate-700/50 px-4 py-2 rounded-lg mb-2">
                      <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                        {getDayNameFa(dayjs(dateKey).day())} {formatDateLocalized(dateKey, calendar, language)}
                      </p>
                    </div>
                    <div className="space-y-2">
                      {dateTasks.map((task) => (
                        <div
                          key={task.id}
                          className={`flex items-center gap-4 p-4 rounded-lg border-r-4 transition-all ${
                            PRIORITY_COLOR[task.priority]
                          } ${task.done ? 'opacity-60' : ''}`}
                        >
                          <button
                            onClick={() => toggleTask(task.id)}
                            className={`flex-shrink-0 w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all ${
                              task.done
                                ? 'bg-green-500 border-green-500 text-white'
                                : 'border-gray-300 dark:border-gray-500 hover:border-green-500'
                            }`}
                          >
                            {task.done && '✓'}
                          </button>
                          <div className="flex-1 min-w-0">
                            <p
                              className={`text-lg font-semibold ${
                                task.done
                                  ? 'line-through text-gray-500 dark:text-gray-400'
                                  : 'text-gray-800 dark:text-white'
                              }`}
                            >
                              {task.title}
                            </p>
                            <div className="flex flex-wrap gap-2 mt-2">
                              <span className="text-xs bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-gray-300 px-2 py-1 rounded">
                                {getCategoryLabel(task.category)}
                              </span>
                              <span className="text-xs text-gray-600 dark:text-gray-400">
                                {getPriorityStars(task.priority)}
                              </span>
                            </div>
                          </div>
                          <button
                            onClick={() => deleteTask(task.id)}
                            className="flex-shrink-0 text-red-500 hover:text-red-700 text-xl transition-colors"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {viewMode === 'priority' && (
              <div className="space-y-6">
                {PRIORITIES.map(priority => {
                  const tasksOfPriority = tasksByPriority[priority]
                  return (
                    <div key={priority}>
                      <div className="flex items-center gap-2 px-4 py-2 mb-3">
                        <span className="text-xl">{getPriorityStars(priority)}</span>
                        <h3 className="text-lg font-bold text-gray-800 dark:text-white">
                          {getPriorityLabel(priority)} ({tasksOfPriority.length})
                        </h3>
                      </div>
                      <div className="space-y-2">
                        {tasksOfPriority.length === 0 ? (
                          <p className="text-sm text-gray-500 dark:text-gray-400 px-4">هیچ وظیفه‌ای نیست</p>
                        ) : (
                          tasksOfPriority.map((task) => (
                            <div
                              key={task.id}
                              className={`flex items-center gap-4 p-4 rounded-lg border-r-4 transition-all ${
                                PRIORITY_COLOR[task.priority]
                              } ${task.done ? 'opacity-60' : ''}`}
                            >
                              <button
                                onClick={() => toggleTask(task.id)}
                                className={`flex-shrink-0 w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all ${
                                  task.done
                                    ? 'bg-green-500 border-green-500 text-white'
                                    : 'border-gray-300 dark:border-gray-500 hover:border-green-500'
                                }`}
                              >
                                {task.done && '✓'}
                              </button>
                              <div className="flex-1 min-w-0">
                                <p
                                  className={`text-lg font-semibold ${
                                    task.done
                                      ? 'line-through text-gray-500 dark:text-gray-400'
                                      : 'text-gray-800 dark:text-white'
                                  }`}
                                >
                                  {task.title}
                                </p>
                                <div className="flex flex-wrap gap-2 mt-2">
                                  <span className="text-xs bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-gray-300 px-2 py-1 rounded">
                                    {getCategoryLabel(task.category)}
                                  </span>
                                  <span className="text-xs text-gray-600 dark:text-gray-400">
                                    {formatDateLocalized(task.date || dayjs().format('YYYY-MM-DD'), calendar, language)}
                                  </span>
                                </div>
                              </div>
                              <button
                                onClick={() => deleteTask(task.id)}
                                className="flex-shrink-0 text-red-500 hover:text-red-700 text-xl transition-colors"
                              >
                                ✕
                              </button>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </>
        )}
      </div>
      </div>
    </div>
  )
}
